type Node = {
	type: string;
	[key: string]: unknown;
};

export type ProgramNode = Node & {
	body: Node[];
};

const isNode = (value: unknown): value is Node =>
	typeof value === "object" && value !== null && "type" in value;

const isReturnWithFunction = (node: Node): boolean => {
	const argument = node?.["argument"];
	if (!isNode(argument)) {
		return false;
	}
	return (
		argument.type === "FunctionExpression" ||
		argument.type === "ArrowFunctionExpression"
	);
};

const isFunctionDeclaration = (node: Node): boolean =>
	node?.type === "FunctionDeclaration";

const isExportedFunction = (node: Node): boolean => {
	if (node?.type !== "ExportDefaultDeclaration") {
		return false;
	}

	const declaration = node["declaration"];
	if (!isNode(declaration)) {
		return false;
	}
	return (
		declaration.type === "FunctionDeclaration" ||
		declaration.type === "FunctionExpression"
	);
};

const createReturnUndefined = (): Node => ({
	type: "ReturnStatement",
	argument: { type: "Identifier", name: "undefined" },
});

const createReturnFromIdentifier = (identifier: string): Node => ({
	type: "ReturnStatement",
	argument: { type: "Identifier", name: identifier },
});

const functionExpressionFromDeclaration = (fn: Node): Node => {
	const params = Array.isArray(fn["params"]) ? fn["params"] : [];
	const id = isNode(fn["id"]) ? fn["id"] : null;

	return {
		type: "FunctionExpression",
		id,
		params,
		body: fn["body"],
		generator: Boolean(fn["generator"]),
		async: Boolean(fn["async"]),
	};
};

const createReturnFromFunction = (fn: Node): Node => ({
	type: "ReturnStatement",
	argument:
		fn.type === "FunctionExpression"
			? fn
			: functionExpressionFromDeclaration(fn),
});

/**
 * Normalizes a parsed program so that it always produces a return value for the sandbox wrapper.
 * The transformation is intentionally conservative – it only rewrites the shapes we explicitly support
 * (return function, named function declaration, and export default function) and leaves other code untouched.
 */
export const rewriteProgram = (program: ProgramNode): ProgramNode => {
	const originalBody: Node[] = Array.isArray(program.body) ? program.body : [];
	const statements: Node[] = originalBody.filter(
		(node) => node?.type !== "EmptyStatement",
	);

	if (statements.length === 0) {
		return { ...program, body: [createReturnUndefined()] };
	}

	// Case 1: return function (...) { ... }
	const returnWithFunction = statements.find(
		(node) => node.type === "ReturnStatement" && isReturnWithFunction(node),
	);
	if (returnWithFunction) {
		return { ...program, body: statements };
	}

	// Case 2: export default function (...) { ... }
	const exportIndex = statements.findIndex(isExportedFunction);
	if (exportIndex !== -1) {
		const exported = statements[exportIndex];
		const declaration = exported["declaration"];
		if (!isNode(declaration)) {
			return program;
		}

		const before = statements.slice(0, exportIndex);
		const after = statements.slice(exportIndex + 1);
		const returnStatement = createReturnFromFunction(declaration);

		return {
			...program,
			body: [...before, returnStatement, ...after],
		};
	}

	// Case 3: named function declaration – append `return <identifier>;`
	const functionDeclaration = [...statements]
		.reverse()
		.find(isFunctionDeclaration);
	if (functionDeclaration) {
		const fnIdentifier = functionDeclaration["id"];
		const identifier =
			isNode(fnIdentifier) && typeof fnIdentifier["name"] === "string"
				? (fnIdentifier["name"] as string)
				: undefined;
		if (identifier) {
			return {
				...program,
				body: [...statements, createReturnFromIdentifier(identifier)],
			};
		}
	}

	// Fallback: leave program as-is. The caller may still decide to wrap raw code or throw.
	return { ...program, body: statements };
};
